export type EntityId = string

type UnknownRecord = Record<string, unknown>

const isRecord = (value: unknown): value is UnknownRecord => typeof value === 'object' && value !== null

/**
 * Checks whether a value contains any of the given `ids`.
 *
 * Used by the cache-invalidation hook for both `queryKey` and cached `data`
 * id-scans. Recursion is depth-bounded and cycle-guarded.
 */
export function containsAnyEntityId(value: unknown, ids: ReadonlySet<EntityId>): boolean {
  // Fast path for primitives
  if (value == null) return false
  if (typeof value === 'string') return ids.has(value)
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return false

  // Avoid infinite recursion
  const visited = new Set<unknown>()

  const walk = (v: unknown, depth: number): boolean => {
    if (v == null) return false
    if (typeof v === 'string') return ids.has(v)
    if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return false

    if (depth <= 0) return false
    if (visited.has(v)) return false
    visited.add(v)

    if (Array.isArray(v)) {
      for (const item of v) {
        if (walk(item, depth - 1)) return true
      }
      return false
    }

    if (isRecord(v)) {
      // Common pattern: entities / resources have an `id` field.
      const maybeId = v.id
      if (typeof maybeId === 'string' && ids.has(maybeId)) return true

      for (const val of Object.values(v)) {
        if (walk(val, depth - 1)) return true
      }
    }

    return false
  }

  // Depth is a tradeoff between correctness and cost.
  // We keep it small because this function can be called for many cached queries.
  return walk(value, 6)
}
